import React, {useEffect, useMemo, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {v4 as uuidv4} from 'uuid';
import {AssetStatus, UserAssetResponseAdapter} from '@taopix/taopix-core';
import {AssetChild, AssetMetaInterface, ProjectAsset} from '@taopix/taopix-core/dist/types/types';
import {Button, MatchesMediaQuery} from '@taopix/taopix-design-system';
import {sortImages} from '../../Utils/MediaGallery';
import {AssetServicesActions} from '../Context/AssetServices/AssetServicesActions';
import {useAssetServices} from '../Context/AssetServices/AssetServicesContext';
import MediaGallery, {HideMode} from '../MediaGallery/MediaGallery';
import {PreviousUploadComponentProps} from './PreviousUploads/PreviousUploadsFactory';
import LoadOrFailPanel from './Views/LoadOrFailPanel';
import {PictureSortDirection} from '../MediaGallery/MediaGalleryOptionsPopout';

type AssetFPOList = {
  assets: Record<number, AssetChild>;
};

type PreviousUploadsResponse = {
  assets: Record<number, ProjectAsset>;
};

type PreviousUploadWindowProps = PreviousUploadComponentProps & {
  sortDirection: PictureSortDirection;
  onPopulated: () => void;
};

const PreviousUploads = ({
  usage,
  server,
  settings,
  projectImages,
  hideUsed,
  sortType,
  sortDirection,
  thumbnailLayout,
  onPopulated,
}: PreviousUploadWindowProps) => {
  const {t} = useTranslation(['*', 'assetservices', 'picturemanager', 'helptips']);
  const services = useAssetServices();
  const [assets, setAssets] = useState<AssetMetaInterface[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const itemsPerPage = 100;
  const smallScreen = MatchesMediaQuery('(max-width: 799px)');
  const verySmallScreen = MatchesMediaQuery('(max-width:400px)');

  const currentItems = useMemo(() => {
    return sortImages(sortType, sortDirection, [...assets]).slice(0, page * itemsPerPage);
  }, [assets, sortType, sortDirection, page, itemsPerPage]);

  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setLoading(true);
    server
      .get<PreviousUploadsResponse>('/api/assets/previous', {token: 'token', json: true})
      .then(({data}) => {
        const payload = Object.values(data.assets);
        const length = payload.length;
        if (!length) {
          return;
        }
        setTotalPages(Math.ceil(length / itemsPerPage));
        setAssets([
          ...UserAssetResponseAdapter(
            payload.filter(
              a =>
                a.children.length &&
                a.status >= AssetStatus.Uploaded &&
                a.status !== AssetStatus.UseFromHostsServer
            ),
            settings
          ),
          ...payload
            .filter(
              a =>
                !a.children.length &&
                a.status >= AssetStatus.Uploaded &&
                a.status !== AssetStatus.UseFromHostsServer
            )
            .map(a => {
              return {
                ...a,
                ref: uuidv4(),
              };
            }),
        ] as AssetMetaInterface[]);
      })
      .catch(error => {
        // show boundary
      })
      .finally(() => {
        setLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!loading && assets.length > 0) {
      onPopulated();
    }
  }, [loading]);

  useEffect(() => {
    const childless = currentItems
      .filter(a => !a?.children.length && AssetStatus.Processed === a.status)
      .map(a => a.id);

    if (!childless.length) {
      return;
    }

    setLoading(true);
    server
      .post<AssetFPOList>(
        '/api/assets/fpo',
        {
          assetIds: childless,
        },
        {token: 'token', json: true}
      )
      .then(({data}) => {
        const updates = currentItems
          .filter(image => !image.children.length && image.status === AssetStatus.Processed)
          .map(image => {
            if (data.assets[image.id]) {
              // return the updated asset record
              return {
                ...image,
                children: data.assets[image.id],
              };
            }
            return null;
          })
          .filter(i => i);

        const updated = UserAssetResponseAdapter(updates as any, settings).filter(
          image => AssetStatus.Error !== image.status
        ) as AssetMetaInterface[];

        if (updated.length) {
          onLoadAssets(updated);
        }

        setLoading(false);
      })
      .catch(error => {
        if (server.isCancel(error)) {
          console.error(error);
        }
      });
  }, [currentItems]);

  useEffect(() => {
    if (!assets.length) {
      return;
    }
    // when the sort type/direction changes, set the page back to 1
    setPage(1);
  }, [sortType, sortDirection]);

  const onLoadAssets = (updates: AssetMetaInterface[]) => {
    setAssets(current =>
      current
        .map(asset => {
          const update = updates.find(u => asset.id === u.id);
          if (update) {
            return update;
          }
          return asset;
        })
        .filter(asset => AssetStatus.Error !== asset.status)
    );
  };

  const onSelectAsset = (checked: boolean, pic: AssetMetaInterface) => {
    // create a map of the current selected assets
    const selected = new Map<number, AssetMetaInterface>();
    services.state.assets.forEach(a => {
      selected.set(a.id, a);
    });

    if (AssetStatus.Error === pic.status) {
      return void 0;
    }
    if (checked) {
      if (!selected.has(pic.id)) {
        selected.set(pic.id, pic);
      }
    } else {
      if (selected.has(pic.id)) {
        selected.delete(pic.id);
      }
    }
    services.dispatch(AssetServicesActions.setSelectedAssets(Array.from(selected.values())));
  };

  const onSelectGroup = (checked: boolean, payload: any[]) => {
    // create a map of the current selected assets
    const selected = new Map<number, AssetMetaInterface>();
    services.state.assets.forEach(a => {
      selected.set(a.id, a);
    });

    payload.forEach(pic => {
      if (AssetStatus.Error === pic.status) {
        return;
      }
      if (checked) {
        if (!selected.has(pic.id)) {
          selected.set(pic.id, pic);
        }
      } else {
        if (selected.has(pic.id)) {
          selected.delete(pic.id);
        }
      }
    });
    services.dispatch(AssetServicesActions.setSelectedAssets(Array.from(selected.values())));
  };

  return (
    <div className={'overflow-auto flex flex-1 flex-col'} ref={containerRef}>
      {0 === assets.length ? (
        <LoadOrFailPanel
          status={loading ? 'loading' : 'failed'}
          failureMessage={t('str_MessageNoPreviousUploads', {
            ns: 'assetservices',
          })}
        />
      ) : (
        <>
          <MediaGallery
            groupedView={true}
            selectableImages={true}
            hideUsed={hideUsed}
            thumbnailLayout={thumbnailLayout}
            sortBy={sortType}
            sortDirection={sortDirection}
            imageList={currentItems}
            pictureUsage={usage}
            onSelectPicture={onSelectAsset}
            onSelectGroup={onSelectGroup}
            selectedImages={services.state.assets.map(a => a.id)}
            disabledAssetIds={projectImages.map(i => i.id)}
            thumbnailSize={
              thumbnailLayout === 'details-list'
                ? verySmallScreen
                  ? 90
                  : smallScreen
                  ? 120
                  : 160
                : 100
            }
            currencyCode={settings.currency.code}
            hideMode={HideMode.Project}
          />
          {page < totalPages && (
            <div className={'flex flex-1 justify-center items-center mt-10'}>
              <Button
                buttonStyle={'distinct'}
                className={'self-center'}
                label={t('helptips:str_ButtonLoadMore')}
                onClick={() => {
                  setPage(current => {
                    const newPage = current + 1;
                    if (newPage > totalPages) {
                      return current;
                    }
                    return newPage;
                  });
                }}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default PreviousUploads;
